import { Typography } from '@material-ui/core'
import styles from './Charity.module.scss'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SegmentedControl from '../../components/SegmentedControl'
import { centsToDollars, centsToFixedDollars } from '../../utils/formatters'
import Button from '../../components/Button'
import {
  getCharityDescription,
  getCharityImage,
  getCharityName,
  getCharityOptions,
  getCharityOptionsEnabled,
  getRoundUpForCharityEnabled,
} from '../../selectors/menus'
import { getNextDollar } from '../../utils/charityConfig'
import BackButton from '../components/BackButton'
import { setVNTipEntryDialog } from '../../VNDialogs/ActionCreators'
import { isEmpty } from 'lodash'
import { getOrderInProgress } from '../../selectors/order'
import { isTabbed } from '../../utils/orderStates'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import useCreateOrderInProgress from '../../hooks/useCreateOrderInProgress'
import { addDonationsToOrderInProgress } from '../../actions/order'

export const KioskCharity = ({ onDone, onBack = null, totalInCents }) => {
  const classNames = cn(styles.charity)
  const [createOrder] = useCreateOrderInProgress()
  const dispatch = useDispatch()
  const history = useHistory()
  const [donationId, setDonationId] = useState(null)
  const [donationAmount, setDonationAmount] = useState(0)

  const addCharityThenRedirect = history?.location?.state?.addCharityThenRedirect || false

  const charityOptions = useSelector(getCharityOptions)
  const charityTitle = useSelector(getCharityName)
  const charityDescription = useSelector(getCharityDescription)
  const optionsEnabled = useSelector(getCharityOptionsEnabled)
  const roundUpEnabled = useSelector(getRoundUpForCharityEnabled)
  const charityImage = useSelector(getCharityImage)
  const order = useSelector(getOrderInProgress)
  const isTabOrder = isTabbed(order)

  const [donations, setDonations] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [charityOptionsEnabled, setCharityOptionsEnabled] = useState(false)

  // used for testing
  // const charityDescription = "Support a cause that matters to you. Your donation will help make a difference in the lives of those in need."
  // const charityImage = "https://assets.tiltify.com/uploads/cause/avatar/3842/blob-7f295399-2278-4483-ab44-21e5a55d2f0f.jpeg"
  // const optionsEnabled = true
  // const charityOptions = [
  // {
  //   title: '$1',
  //   price: 100,
  // },
  // {
  //   title: '$5',
  //   price: 500,
  // },
  // {
  //   title: '$10',
  //   price: 1000,
  // },
  // {
  //   price: null,
  //   isCustom: true,
  //   title: 'Custom'
  // },
  // ]

  useEffect(() => {
    if (!isTabOrder && addCharityThenRedirect) {
      createOrder()
    }
  }, [createOrder, addCharityThenRedirect, isTabOrder])

  const transformCharityOptions = (options) => {
    return options.map((option, index) => {
      return option.title || `${centsToFixedDollars(option.price)}`
    })
  }

  useEffect(() => {
    if (isEmpty(charityOptions)) {
      setCharityOptionsEnabled(false)
      return
    }

    setCharityOptionsEnabled(optionsEnabled)
    setDonations(transformCharityOptions(charityOptions))
  }, [optionsEnabled, charityOptions])

  const goBack = () => {
    if (addCharityThenRedirect) {
      history.goBack()
    }
    if (onBack) {
      onBack()
    }
  }

  const onButtonContainerClick = (index) => {
    const selectedDonation = donations[index]
    const donationObject = charityOptions.find(
      (donation) => (donation.title || `${centsToFixedDollars(donation.price)}`) === selectedDonation,
    )

    if (donationObject?.isCustom) {
      // open custom donation screen
      dispatch(
        setVNTipEntryDialog(
          true,
          (donationAmountInCents) => {
            dispatch(setVNTipEntryDialog(false))
            setDonationAmount(donationAmountInCents)
            setSelectedIndex(index)
            setDonationId(index)
          },
          donationId === index ? donationAmount : 0,
          'Custom Donation',
          true,
          'Save',
        ),
      )
      return
    }

    setDonationId(index)
    const donationAmountInCents = donationObject?.price || 0
    setDonationAmount(donationAmountInCents)
    setSelectedIndex(index)
  }

  const onConfirm = (donation = null) => {
    let amount = donation ?? donationAmount

    if (roundUpEnabled && !charityOptionsEnabled && donation === null) {
      // if not null the user may have selected no thank you
      amount = getNextDollar(totalInCents)
    }

    if (addCharityThenRedirect) {
      dispatch(addDonationsToOrderInProgress(amount))
      history.push(addCharityThenRedirect)
    }

    onDone(amount)
  }

  const donationText = () => {
    if (!charityOptionsEnabled) {
      return `Round Up & Give ${centsToFixedDollars(getNextDollar(totalInCents))}`
    }

    return `Donate ${centsToFixedDollars(donationAmount)}`
  }

  const CharityHeader = () => {
    if (charityImage) {
      return <img src={charityImage} alt="" className={styles.charityImage} />
    }

    if (charityTitle) {
      return <Typography className={styles.charityText}>{charityTitle}</Typography>
    }

    return null
  }

  return (
    <div className={classNames}>
      <div>
        <div className={styles.gratuityHeaderKiosk}>
          <BackButton
            handleKeyDown={onBack}
            ariaLabel={centsToDollars(totalInCents + donationAmount)}
            // refProp={backButtonRef}
            className={styles.backButton}
            withIcon={true}
            onClick={goBack}
            iconStyle={styles.backButtonIcon}
          >
            <div className={styles.orderTotal}>{centsToDollars(totalInCents + donationAmount)}</div>
          </BackButton>
          {
            <span className={cn('payment__header', styles.primaryHeader)}>
              {charityOptionsEnabled ? 'SUPPORT A CAUSE?' : 'ROUND UP TO SUPPORT A CAUSE?'}
            </span>
          }
        </div>
      </div>
      {charityOptionsEnabled && (
        <SegmentedControl items={donations} selectedIndex={selectedIndex} onChange={onButtonContainerClick} isKiosk={true} />
      )}
      <div className={styles.centerColumn}>
        <CharityHeader />
        {charityDescription && <Typography className={styles.charitySubtext}>{charityDescription}</Typography>}
      </div>
      <div className={styles.bottomButtonContainer}>
        <Button onClick={() => onConfirm(0)} className={cn(styles.noThanksButton, styles.completeButtonKiosk)}>
          No Thanks
        </Button>
        <Button onClick={() => onConfirm(null)} className={styles.completeButtonKiosk}>
          {donationText()}
        </Button>
      </div>
    </div>
  )
}
